import { buildInteractiveResult } from '@coveo/headless';
import * as customPropTypes from 'custom-prop-types';
import PropTypes from 'prop-types';
import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { Loading } from '@/design-system/atoms/Loading';
import { Map } from '@/design-system/atoms/Map';
import { LocationDetail } from '@/design-system/molecules/LocationDetail';
import { mappingWorkingHours } from '@/utils/mappingWorkingHours';

import {
    useLocationSearchContext,
    useLocationSearchZipCodeErrorContext,
} from '../../contexts/LocationSearchContext';
import { useLocationUIContext } from '../../contexts/LocationUIContext';
import { useLocationViewState } from '../../hooks/useLocationViewState';
import styles from './LocationResultsList.module.scss';

const LocationResultsList = ({ cta, analytics }) => {
    const {
        coveo: {
            engine,
            controller: { resultList, querySummary },
        },
        noResultsComponent,
        errorComponent,
    } = useLocationSearchContext();

    const { googleMaps } = useLocationUIContext();

    const viewState = useLocationViewState();

    const zipCodeServerError = useLocationSearchZipCodeErrorContext();

    const [resultListState, setResultListState] = React.useState({});
    const [results, setResults] = React.useState([]);
    const [querySummaryState, setQuerySummaryState] = React.useState(querySummary.state);

    React.useEffect(() => {
        return resultList.subscribe(() => {
            setResultListState(resultList.state);

            const newResults = resultList.state.results.map((s) => {
                const interactiveResultController = engine
                    ? buildInteractiveResult(engine, {
                          options: { result: s },
                      })
                    : null;

                let parsedWorkingHours = null;
                let parsedAdjustedHours = null;

                try {
                    parsedWorkingHours = s.raw.locworkinghours && JSON.parse(s.raw.locworkinghours);
                } catch (e) {
                    console.error(e);
                }

                try {
                    parsedAdjustedHours =
                        s.raw.locadjustedhours && JSON.parse(s.raw.locadjustedhours);
                } catch (e) {
                    console.error(e);
                }

                let workingHours = mappingWorkingHours(
                    parsedWorkingHours,
                    parsedAdjustedHours,
                    `${s.raw.locopen24hours}`.toLowerCase() === 'true',
                );

                for (const key in workingHours) {
                    const value = workingHours[key];
                    workingHours[key] = {
                        ...value,
                        closed: `${value.closed}`.toLowerCase() === 'true',
                    };
                }
                const temporarilyClosed = `${s.raw.loctemporarilyclosed}`.toLowerCase() === 'true';

                let image;
                if (s.raw.locimgurl) {
                    image = {
                        defaultImgSrc: s.raw.locimgurl,
                    };
                }

                let address;
                if (
                    [
                        s.raw.locaddrline1,
                        s.raw.locaddrline2,
                        s.raw.locaddrline3?.split(',')[0],
                        s.raw.locationstate?.[0],
                        s.raw.locationzip?.[0],
                        s.raw.locmapurl,
                    ].filter(Boolean).length > 0
                ) {
                    address = {
                        line1: s.raw.locaddrline1,
                        line2: s.raw.locaddrline2,
                        city: s.raw.locaddrline3?.split(',')[0],
                        state: s.raw.locationstate?.[0],
                        zipCode: s.raw.locationzip?.[0],
                        googleMapsUrl: s.raw.locmapurl,
                    };
                }

                const content = {
                    variant: s.raw.locvariant,
                    locationType: s.raw.locvariant,
                    id: s.uniqueId,
                    title: s.raw.locname || s.title,
                    buildingImage: image,
                    parentLocationName: s.raw.locparent,
                    rootLocationName: s.raw.locroot,
                    buildingName: s.raw.locbuildingname,
                    address,
                    phoneNumber: s.raw.locphnum1,
                    cta: {
                        label: 'View Location Details',
                        href: s.uri,
                    },
                    workingHours,
                    temporarilyClosed,
                    overrideMessage: s.raw.locoverridemessage,
                    waitTime: `${s.raw.locwaittime}`.toLowerCase() === 'true',
                    epicDepartmentId: s.raw.locepicdepartmentid,
                    position: {
                        lat: s.raw.latitude,
                        lng: s.raw.longitude,
                    },
                    coveoAnalyticsCallbacks: interactiveResultController
                        ? {
                              cancelPendingSelect: () =>
                                  interactiveResultController.cancelPendingSelect(),
                              onClick: () => interactiveResultController.select(),
                              onContextMenu: () => interactiveResultController.select(),
                              onMouseDown: () => interactiveResultController.select(),
                              onMouseUp: () => interactiveResultController.select(),
                              onTouchStart: () => interactiveResultController.beginDelayedSelect(),
                              onTouchEnd: () => interactiveResultController.cancelPendingSelect(),
                          }
                        : undefined,
                };

                return {
                    ...content,
                    content,
                };
            });

            setResults(newResults);
        });
    }, [resultList, engine]);

    React.useEffect(() => {
        const unsubscribe = querySummary.subscribe(() => {
            setQuerySummaryState(querySummary.state);
        });
        return () => {
            // Unsubscribe from the query summary state
            unsubscribe();
        };
    }, [querySummary]);

    const handleLoadMoreClick = React.useCallback(
        (e) => {
            e.preventDefault();

            resultList.fetchMoreResults();
        },
        [resultList],
    );

    const state = [
        { state: 'error', valid: resultListState.hasError || zipCodeServerError },
        { state: 'loading', valid: resultListState.isLoading },
        { state: 'noResults', valid: results.length === 0 },
    ].find(({ valid }) => valid)?.state;

    const showResults = results.length > 0;

    if (!state && showResults === false) {
        return null;
    }

    return (
        <>
            <div className={styles['location-results-list']}>
                {showResults && viewState === 'result-list' && (
                    <ul>
                        {results?.map((r) => (
                            <li key={r.id}>
                                <LocationDetail
                                    {...r}
                                    variant={LocationDetail.VARIANT.LIST_ITEM}
                                    analytics={analytics}
                                />
                            </li>
                        ))}
                    </ul>
                )}

                {showResults && viewState === 'maps' && (
                    <Map
                        items={results}
                        apiKey={googleMaps.apiKey}
                        mapId={googleMaps.mapId}
                        analytics={analytics}
                    />
                )}

                {state === 'error' && <>{errorComponent}</>}

                {state === 'loading' && (
                    <div className={styles['location-results-list__overlay']}>
                        <Loading />
                    </div>
                )}

                {state === 'noResults' && <>{noResultsComponent}</>}
            </div>
            {showResults && resultListState.moreResultsAvailable && viewState === 'result-list' && (
                <div className={styles['location-results-list__load-more']}>
                    <Button
                        label={cta?.label ? cta.label : 'Load More'}
                        ariaLabel={cta?.ariaLabel ? cta.ariaLabel : 'Load More Results'}
                        buttonStyle={Button.STYLE.SECONDARY}
                        onClick={handleLoadMoreClick}
                        disabled={resultListState.isLoading}
                        analytics={{
                            ...analytics,
                            eventName: 'search',
                            componentName: 'Locations Search',
                            searchTerm: querySummaryState.query,
                        }}
                    />
                </div>
            )}
        </>
    );
};

LocationResultsList.propTypes = {
    /**
     * Call to action for loading more results
     */
    cta: PropTypes.shape(Button.propTypes),
    analytics: customPropTypes.analyticsPropType,
};

export default LocationResultsList;
